import GeneralModal from "../GeneralModal";
import { WRITE_OP } from "@avenueops/frontend-shared";
import { ClientWorkflow, UpdateDetailsInput } from "@avenueops/shared-types";
import { IconButton, Popover, PopoverContent, PopoverTrigger, SmartForm } from "@avenueops/web-components";
import { getDefaultCreateWorkflowInput } from "components/monitors/editFormReboot/MonitorEditForm";
import EmojiPicker from "components/inputs/EmojiPicker";
import {
  EllipsisVerticalIcon,
  Square2StackIcon,
  PauseIcon,
  PencilSquareIcon,
  PlayIcon,
  TrashIcon,
  BoltIcon,
  DocumentTextIcon,
  BookmarkIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import { WorkflowAlertingConditions, WorkflowStatus, WorkflowTriggerType } from "@prisma/client";
import { useAbilities, useOrganization } from "context/OrganizationContext";
import Link from "next/link";
import { useRouter } from "next/router";
import { useState } from "react";
import { toast } from "react-toastify";
import { pagesPath } from "utils/$path";
import { toastError } from "utils/toast";
import { isTRPCClientError, trpc2 } from "utils/trpc";
import { getDefaultEscalationPolicyInput } from "components/monitorDetails/sections/MonitorEscalationPolicy";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm, useFormState } from "react-hook-form";

interface Props {
  disabled?: boolean;
  workflow: ClientWorkflow;
  includePauseResume?: boolean;
}

export default function MonitorActionMenu({ disabled, workflow, includePauseResume }: Props) {
  const router = useRouter();
  const organization = useOrganization();
  const abilities = useAbilities();
  const utils = trpc2.useContext();
  const canWriteMonitor = abilities.can(WRITE_OP, "Workflow");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const defaultValues: UpdateDetailsInput = {
    title: workflow.title,
    description: workflow.description ?? undefined,
    workflowId: workflow.id!,
    emoji: workflow.emoji,
  };
  const formMethods = useForm({ defaultValues, resolver: zodResolver(UpdateDetailsInput), mode: "onSubmit" });
  const { control, reset, setValue, handleSubmit } = formMethods;

  const { mutateAsync: updateDetails } = trpc2.v2.workflows.updateDetails.useMutation({
    onSuccess: () => {
      utils.v2.workflows.get.invalidate();
      reset(undefined, { keepValues: true });
    },
    onError: () => {
      utils.v2.workflows.get.invalidate();
      setValue("title", workflow.title);
    },
  });

  const onSubmit = async (data: { title: string; description?: string; emoji: string; workflowId: string }, event: any) => {
    try {
      await updateDetails(data, event);
    } catch (err: any) {
      if (isTRPCClientError(err)) {
        toast.error("Monitor title is required.");
      } else {
        toast.error("Something went wrong. Please try again later.");
      }
    }
  };

  const { mutateAsync: duplicateWorkflow } = trpc2.v2.workflows.duplicate.useMutation({
    onSuccess: (duplicatedWorkflow) => {
      router.push(pagesPath.monitors._id(duplicatedWorkflow.id).$url());
    },
    onError: (error) => {
      toastError(error, "There was a problem duplicating the monitor. Please try again later.");
    },
  });

  const { isLoading: isDeleteLoading, mutateAsync: deleteMutation } = trpc2.v2.workflows.delete.useMutation({
    onSuccess: () => {
      toast.success("Monitor was deleted!");
      router.push("/");
    },
    onError: (error) => {
      toastError(error, "There was a problem deleting the monitor. Please try again later.");
    },
  });

  const { mutateAsync: pauseMutation } = trpc2.v2.workflows.pause.useMutation({
    onSuccess: () => {
      utils.v2.workflows.get.invalidate();
    },
  });

  const { mutateAsync: resumeMutation } = trpc2.v2.workflows.resume.useMutation({
    onSuccess: () => {
      utils.v2.workflows.get.invalidate();
    },
  });

  const { mutateAsync: triggerSignalMutation } = trpc2.v2.workflows.triggerSignal.useMutation({
    onSuccess: () => {
      toast.success(
        `${
          workflow.alertingConditions === WorkflowAlertingConditions.NEW_ROW
            ? "Success! If there are any changes detected, a signal will be triggered."
            : "Successfully sent report!"
        }`,
      );
    },
  });

  const { isLoading: updateAlertingConditionsLoading, mutateAsync: updateAlertingConditions } = trpc2.v2.workflows.updateAlertingConditions.useMutation({
    onSuccess: () => {
      utils.v2.workflows.get.invalidate();
    },
  });

  const isDisabled = !canWriteMonitor || isDeleteLoading || disabled;
  const isSqlMonitor =
    workflow?.triggerType === WorkflowTriggerType.RAW_SQL ||
    workflow?.triggerType === WorkflowTriggerType.DATA_VIEW ||
    workflow?.triggerType === WorkflowTriggerType.VISUAL;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <IconButton Icon={EllipsisVerticalIcon} type="OUTLINE" disabled={isDisabled} />
      </PopoverTrigger>
      <PopoverContent className="py-0 px-0" align="end">
        <div className="flex flex-col divide-y">
          <div className="!text-gray-800" role="none">
            {isSqlMonitor ? (
              <Link
                href={pagesPath.monitors._id(workflow.id!).update.$url()}
                passHref
                data-test="monitor-edit-button"
                className="flex flex-row py-2 px-2 text-sm hover:bg-gray-50 w-full"
              >
                <PencilSquareIcon className="h-5 w-5 mr-1" />
                Edit SQL trigger
              </Link>
            ) : null}
            <button className="flex flex-row py-2 px-2 text-sm hover:bg-gray-50 w-full" onClick={async () => setShowDetailsModal(true)}>
              <PencilIcon className="h-5 w-5 mr-1" />
              Edit title
            </button>
            <GeneralModal
              modalVisible={showDetailsModal}
              setModalVisible={setShowDetailsModal}
              title="Edit monitor details"
              onConfirm={handleSubmit(onSubmit)}
              onCancel={() => {
                reset({ title: workflow.title, description: workflow.description ?? undefined, emoji: workflow.emoji });
              }}
            >
              <FormProvider {...formMethods}>
                <div className="flex flex-col gap-2">
                  <div className="w-full flex flex-row gap-2 items-end min-w-[550px]">
                    <div className="flex-1">
                      <SmartForm.TextInput name="title" title="Title" placeholder="Enter a title..." />
                    </div>
                    <div className="my-1">
                      <EmojiPicker hideLabel name="emoji" control={control} />
                    </div>
                  </div>
                  <SmartForm.TextArea name="description" placeholder="Description (optional)" title="Description" />
                </div>
              </FormProvider>
            </GeneralModal>

            <button
              data-test="monitor-edit-button"
              className="flex flex-row py-2 px-2 text-sm hover:bg-gray-50 w-full"
              onClick={async () => {
                const duplicatedWorkflow = await duplicateWorkflow({
                  ...getDefaultCreateWorkflowInput({ monitor: workflow, organization }),
                  ...getDefaultEscalationPolicyInput(workflow),
                  title: workflow.title,
                });
              }}
            >
              <Square2StackIcon className="h-5 w-5 mr-1" />
              Duplicate
            </button>
            {includePauseResume ? (
              <div className="flex sm:hidden">
                {workflow?.status === WorkflowStatus.PAUSED &&
                workflow.triggerType !== WorkflowTriggerType.WEBHOOK &&
                workflow.triggerType !== WorkflowTriggerType.INTEGRATION ? (
                  <button
                    className="flex flex-row py-2 px-2 text-sm hover:bg-gray-50 w-full"
                    onClick={async () => await resumeMutation({ workflowId: workflow.id! })}
                  >
                    <PlayIcon className="h-5 w-5 mr-1" />
                    Resume
                  </button>
                ) : workflow?.status === WorkflowStatus.READY &&
                  workflow.triggerType !== WorkflowTriggerType.WEBHOOK &&
                  workflow.triggerType !== WorkflowTriggerType.INTEGRATION ? (
                  <button
                    className="flex flex-row py-2 px-2 text-sm hover:bg-gray-50 w-full"
                    onClick={async () => await pauseMutation({ workflowId: workflow.id! })}
                  >
                    <PauseIcon className="h-5 w-5 mr-1" />
                    Pause
                  </button>
                ) : null}
              </div>
            ) : null}
            {workflow.triggerType !== WorkflowTriggerType.WEBHOOK && workflow.triggerType !== WorkflowTriggerType.INTEGRATION ? (
              <>
                <button className="flex flex-row py-2 px-2 text-sm hover:bg-gray-50 w-full" onClick={() => setShowConvertModal(true)}>
                  {workflow.alertingConditions === WorkflowAlertingConditions.NEW_ROW ? (
                    <>
                      <DocumentTextIcon className="h-5 w-5 mr-1" />
                      Convert to a report monitor
                    </>
                  ) : (
                    <>
                      <BookmarkIcon className="h-5 w-5 mr-1" />
                      Convert to an event monitor
                    </>
                  )}
                </button>
                <GeneralModal
                  modalVisible={showConvertModal}
                  setModalVisible={setShowConvertModal}
                  title={`Convert to ${workflow.alertingConditions === WorkflowAlertingConditions.NEW_ROW ? "a report monitor" : "an event monitor"}`}
                  onConfirm={() =>
                    !updateAlertingConditionsLoading &&
                    updateAlertingConditions({
                      workflowId: workflow.id!,
                      alertingConditions:
                        workflow.alertingConditions === WorkflowAlertingConditions.NEW_ROW
                          ? WorkflowAlertingConditions.PASSTHROUGH
                          : WorkflowAlertingConditions.NEW_ROW,
                    })
                  }
                >
                  <div className="text-gray-600 flex flex-col gap-1">
                    {workflow.alertingConditions === WorkflowAlertingConditions.NEW_ROW ? (
                      <div>Report monitors trigger alerts at a set schedule with all chosen data.</div>
                    ) : (
                      <div>Event monitors trigger alerts when your data changes.</div>
                    )}
                    <div>This monitor will re-baseline if the type is updated.</div>
                  </div>
                </GeneralModal>
              </>
            ) : null}
          </div>
          {workflow.triggerType !== WorkflowTriggerType.WEBHOOK && workflow.triggerType !== WorkflowTriggerType.INTEGRATION ? (
            <button
              className="flex flex-row py-2 px-2 text-sm hover:bg-gray-50 w-full"
              onClick={async () => await triggerSignalMutation({ workflowId: workflow.id! })}
            >
              <BoltIcon className="h-5 w-5 mr-1" />
              {workflow.alertingConditions === WorkflowAlertingConditions.NEW_ROW ? "Check for alerts" : "Send report"}
            </button>
          ) : null}
          <div className="text-red-600" role="none">
            <button className="flex flex-row py-2 px-2 text-sm hover:bg-gray-50 w-full" onClick={() => setShowDeleteModal(true)}>
              <TrashIcon className="h-5 w-5 mr-1" />
              Delete
            </button>
            <GeneralModal
              modalVisible={showDeleteModal}
              setModalVisible={setShowDeleteModal}
              title="Are you sure?"
              onConfirm={() => !isDeleteLoading && deleteMutation({ workflowIds: [workflow.id!] })}
            >
              Deleting this monitor cannot be undone.
            </GeneralModal>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
